import { useEffect, useRef } from 'react';
import './AgriCapabilities.css';

const CURRENT = [
    {
        id: '01',
        head: '3 Drone Models',
        body: '1 spreader and 2 sprayer configurations — each with distinct operational characteristics modelled from real agri drone equipment.',
    },
    {
        id: '02',
        head: '7 Crop Varieties',
        body: 'Seven agricultural crop types are available across the simulation environment, each representing different canopy structures and field layouts.',
    },
    {
        id: '03',
        head: '3 Growth Stages Per Crop',
        body: 'Every crop moves through seeding, pre-harvest, and harvest stages — enabling operators to train across the full agricultural cycle.',
    },
    {
        id: '04',
        head: 'Dynamic Switching',
        body: 'Drone models and crop environments can be switched mid-session without restarting — allowing rapid scenario cycling during training.',
    },
    {
        id: '05',
        head: 'Physics-Based Flight Model',
        body: 'All drone movement — lift, tilt, yaw, and throttle response — is governed by physics logic. No pre-defined animations are used.',
    },
    {
        id: '06',
        head: 'Taranis Q X7 Integration',
        body: 'The FrSky Taranis Q X7 transmitter connects via USB. Middleware maps hardware inputs to real-time control signals inside the simulation.',
    },
    {
        id: '07',
        head: 'Real-Time Control Response',
        body: 'Input from the transmitter or standard controller is processed frame-by-frame, with physics calculations applied at each step.',
    },
    {
        id: '08',
        head: 'Plain Terrain Environment',
        body: 'The current build operates on a flat agricultural field — providing a controlled baseline environment optimised for flight and control training.',
    },
];

const ROADMAP = [
    {
        id: '01',
        head: 'Damage & Breakage System',
        body: 'Collision events and poor flight decisions will carry simulated consequences — introducing operational risk and reinforcing discipline.',
    },
    {
        id: '02',
        head: 'Advanced Terrain Environments',
        body: 'Hilly terrain, waterlogged fields, and uneven agricultural land — expanding training conditions beyond the current plain environment.',
    },
    {
        id: '03',
        head: 'Region-Specific Crops',
        body: 'Crop varieties and farming layouts mapped to specific agricultural regions across India, enabling location-relevant training scenarios.',
    },
    {
        id: '04',
        head: 'Tree Plantation Simulation',
        body: 'Mango orchards, coconut groves, and eucalyptus plantations — introducing canopy navigation and precision application in plantation agriculture.',
    },
    {
        id: '05',
        head: 'Drone Maintenance Module',
        body: 'Pre-flight checks, component inspection, battery management, and post-flight servicing protocols built into the simulation workflow.',
    },
    {
        id: '06',
        head: 'Custom Drone Configurations',
        body: 'Operators will configure drone models matching their real-world fleet — spray systems, tank capacity, nozzle type, and frame setup.',
    },
    {
        id: '07',
        head: 'Custom Agricultural Environments',
        body: 'Institutional partners will be able to request environment configurations tailored to their specific training requirements and regional context.',
    },
];

function useReveal() {
    const refs = useRef([]);
    useEffect(() => {
        const io = new IntersectionObserver(
            entries => entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('sc-visible');
                    io.unobserve(e.target);
                }
            }),
            { threshold: 0.1 }
        );
        refs.current.forEach(el => el && io.observe(el));
        return () => io.disconnect();
    }, []);
    return refs;
}

export default function Capabilities() {
    const refs = useReveal();

    return (
        <section className="sc-wrap" id="capabilities">
            <div className="sc-bg-grid" aria-hidden="true" />

            <div className="sc-inner">

                {/* ── Section eyebrow ── */}
                <div className="sc-eyebrow-row sc-reveal" ref={el => refs.current[90] = el}>
                    <span className="sc-eyebrow-dot" aria-hidden="true" />
                    <span className="sc-eyebrow-text">Platform Overview</span>
                </div>

                {/* ── Section title ── */}
                <div className="sc-title-row sc-reveal" ref={el => refs.current[91] = el}>
                    <h2 className="sc-h2">
                        Current Capabilities
                        <em>& Roadmap</em>
                    </h2>
                    <p className="sc-h2-sub">
                        A structured overview of what Agri Drone Simulator delivers today and
                        what is committed for future development.
                    </p>
                </div>

                {/* ══════════════════════
            CURRENT CAPABILITIES
        ══════════════════════ */}
                <div className="sc-block sc-reveal" ref={el => refs.current[92] = el}>
                    <div className="sc-block-label">
                        <span className="sc-label-badge sc-label-badge--live">
                            <span className="sc-badge-dot" aria-hidden="true" />
                            Available Now
                        </span>
                        <h3 className="sc-block-h3">Current Capabilities</h3>
                    </div>

                    <div className="sc-current-grid">
                        {CURRENT.map((c, i) => (
                            <div
                                key={c.id}
                                className="sc-cap-card sc-reveal"
                                ref={el => refs.current[i] = el}
                            >
                                <div className="sc-cap-card-top">
                                    <span className="sc-cap-id" aria-hidden="true">{c.id}</span>
                                    <div className="sc-cap-check" aria-hidden="true">✓</div>
                                </div>
                                <h4 className="sc-cap-head">{c.head}</h4>
                                <p className="sc-cap-body">{c.body}</p>
                                <div className="sc-cap-line" aria-hidden="true" />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ══════════════════════
            ROADMAP
        ══════════════════════ */}
                <div className="sc-block sc-reveal" ref={el => refs.current[93] = el} style={{ marginTop: '80px' }}>
                    <div className="sc-block-label">
                        <span className="sc-label-badge sc-label-badge--soon">
                            <span className="sc-badge-dot sc-badge-dot--soon" aria-hidden="true" />
                            In Development
                        </span>
                        <h3 className="sc-block-h3">Development Roadmap</h3>
                    </div>

                    <div className="sc-roadmap-list">
                        {ROADMAP.map((r, i) => (
                            <div
                                key={r.id}
                                className="sc-road-item sc-reveal"
                                ref={el => refs.current[20 + i] = el}
                            >
                                <div className="sc-road-left">
                                    <span className="sc-road-id">{r.id}</span>
                                    <div className="sc-road-track" aria-hidden="true">
                                        <div className="sc-road-node" aria-hidden="true" />
                                        {i < ROADMAP.length - 1 && (
                                            <div className="sc-road-line" aria-hidden="true" />
                                        )}
                                    </div>
                                </div>
                                <div className="sc-road-content">
                                    <h4 className="sc-road-head">{r.head}</h4>
                                    <p className="sc-road-body">{r.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Closing line ── */}
                <div className="sc-close sc-reveal" ref={el => refs.current[94] = el}>
                    <div className="sc-close-rule" aria-hidden="true" />
                    <p className="sc-close-text">
                        Agri Drone Simulator is under active development — each release brings the simulation
                        closer to the operational complexity of real-world agri drone work.
                    </p>
                </div>

            </div>
        </section>
    );
}